import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import MiniTaskReplayButton from '../components/MiniTaskReplayButton';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import LetterSequence from './LetterSequence';
import WordAdventureArt from './WordAdventureArt';
import useWordAdventure from './useWordAdventure';
import useTourIdleHelp from './useTourIdleHelp';
import './soundHunt.css';
import './syllableFinal.css';
import './wordAdventure.css';

export default function WordAdventureRoundView(props) {
  const { round, classTitle, onExit } = props;
  const state = useWordAdventure(props);
  const { index, phase, slots, selected, completed, error, wrong, handlers } = state;
  const item = round.items[index];
  const bridge = round.assessment_mode === 'syllable_bridge';
  const chest = round.assessment_mode === 'silent_word_chest';
  const drag = useRef(null), suppressClick = useRef(false);
  const [offset, setOffset] = useState(null);
  useTourIdleHelp(phase === 'ready' && selected === null && !offset, () => handlers.current.help(), slots.join(','));
  const text = (id) => item.stones?.find((stone) => stone.id === id)?.text;
  const pointerDown = (event, id) => {
    if (phase !== 'ready' || slots.includes(id)) return;
    suppressClick.current = false;
    drag.current = { id, pointer: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const pointerMove = (event) => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    const x = event.clientX - current.x, y = event.clientY - current.y;
    if (Math.hypot(x, y) > 8) current.moved = true;
    if (current.moved) setOffset({ id: current.id, x, y });
  };
  const pointerUp = (event) => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    drag.current = null; setOffset(null);
    if (current.moved) {
      suppressClick.current = true;
      const slot = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-adventure-slot]');
      if (slot) handlers.current.place(current.id, Number(slot.dataset.adventureSlot));
    }
  };
  const slotButton = (position) => <button type="button" data-adventure-slot={position}
    className={`adventure-slot ${slots[position] ? 'is-filled' : ''} ${selected ? 'is-awaiting' : ''}`}
    disabled={phase !== 'ready'} onClick={() => handlers.current.slot(position)}
    aria-label={slots[position] ? `${position + 1}. parçayı çıkar` : `${position + 1}. boş yuvaya yerleştir`}>
    {slots[position] ? <LetterSequence value={text(slots[position])} /> : <><span className="adventure-placeholder">?</span><TouchAppRoundedIcon /></>}
  </button>;
  return <Box className={`sound-hunt word-adventure ${round.assessment_mode} ${wrong ? 'has-soft-reset' : ''}`}>
    <GardenBackdrop />
    <Box className="sound-hunt-content">
      <MiniTaskGameHeader classTitle={classTitle} soundEnabled soundRequired />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">{withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi</Typography>
        <Box className="sound-hunt-progress" aria-label={`Soru ${index + 1} / ${round.items.length}`}>{index + 1} / {round.items.length}</Box>
      </Box>
      <Box className="sound-hunt-bottom">
        <Typography className="sound-hunt-badge">{round.badge_title}</Typography>
        <WordAdventureArt mode={round.assessment_mode} completed={completed} total={round.items.length} />
        <Box className="adventure-cue" aria-hidden="true"><TouchAppRoundedIcon />{bridge ? 'Heceyi seç, yuvaya dokun' : chest ? 'Oku ve resmi seç' : 'Eksik parçayı seç'}</Box>
        {!chest && <MiniTaskReplayButton className="adventure-listen" disabled={phase !== 'ready'} label="Sözcüğü yeniden dinle" onClick={() => handlers.current.replay()} />}
        {chest ? <>
          <Box className="adventure-written" role="img" aria-label={`Yazılı sözcük: ${item.written_word}`}><LetterSequence value={item.written_word} /></Box>
          <Box className="adventure-pictures">
            {item.choices.map((choice, i) => <button type="button" key={choice.id} disabled={phase !== 'ready'}
              aria-label={`${i + 1}. resmi seç`} onClick={() => handlers.current.choose(choice.id)}>
              <img src={choice.image.url} alt={choice.semantic_label} draggable="false" />
            </button>)}
          </Box>
        </> : <>
          <Box className={`adventure-slots ${bridge ? 'bridge-slots' : 'repair-slots'}`}>
            {bridge ? <>{slotButton(0)}<span className="adventure-plus" aria-hidden="true">+</span>{slotButton(1)}</>
              : <>{item.prefix && <LetterSequence value={item.prefix} />}{slotButton(0)}{item.suffix && <LetterSequence value={item.suffix} />}</>}
          </Box>
          <Box className="adventure-stones">
            {item.stones.map((stone) => <button type="button" key={stone.id} data-piece={stone.id}
              disabled={phase !== 'ready' || slots.includes(stone.id)} aria-label={`${stone.text} parçasını seç`}
              className={`adventure-stone ${selected === stone.id ? 'is-selected' : ''} ${slots.includes(stone.id) ? 'is-used' : ''} ${offset?.id === stone.id ? 'is-dragging' : ''}`}
              style={offset?.id === stone.id ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined}
              onPointerDown={(event) => pointerDown(event, stone.id)} onPointerMove={pointerMove} onPointerUp={pointerUp}
              onPointerCancel={() => { drag.current = null; setOffset(null); }} onClick={(event) => {
                const suppressed = suppressClick.current && event.detail > 0;
                suppressClick.current = false;
                if (!suppressed) handlers.current.choose(stone.id);
              }}><LetterSequence value={stone.text} /></button>)}
          </Box>
        </>}
        <Box className="sound-hunt-controls">
          <Button className="sound-hunt-control" startIcon={<RecordVoiceOverRoundedIcon />}
            disabled={!['waiting', 'ready', 'retry'].includes(phase)} onClick={() => phase === 'waiting'
              ? handlers.current.start() : phase === 'retry' ? handlers.current.retry() : handlers.current.replay(true)}>
            {phase === 'retry' ? 'Tekrar Dene' : 'Yönergeyi dinleyelim'}
          </Button>
        </Box>
        <Typography role="status" className="sound-hunt-status">{error}</Typography>
        {chest && completed === round.items.length && <Box className="adventure-confetti" aria-hidden="true">{Array.from({ length: 18 }, (_, i) =>
          <i key={i} style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 4) * .12}s` }} />)}</Box>}
      </Box>
    </Box>
  </Box>;
}
